import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { Eye, AlertCircle, Info } from 'lucide-react';
import { Spreadsheet } from '../../spreadsheet';
import { SheetTabs } from '../../spreadsheet/components/SheetTabs';
import { Card, CardContent } from '../../ui/card';
import { LoadingState } from '../../ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { useShifts } from '../../../lib/supabase/shifts/supabaseShiftService';
import { useModelsFixedShifts } from '../../../lib/supabase/shifts/fixed/supabaseFixedShiftService';
import { useModels } from '../../../lib/supabase/models/supabaseModelService';
import { useUsers } from '../../../lib/supabase/users/supabaseUserService';
import { useAuth } from '../../../contexts/AuthContext';
import { useRolesWithPermissions } from '../../../lib/supabase/roles/supabaseRoleService';
import { calculateShiftPatternData, ShiftPatternData } from '@shiftly/shared/src/calculators/staff_report/staffReportCalculator';
import { getMonthBoundary } from '../../../lib/utils';
import StaffReportNotesService, { StaffReportUserNotes, OverwatchRequest } from '../../../lib/supabase/staffReport/supabaseStaffReportService';
import { CustomContextMenuItem } from '../../spreadsheet/types';
import { AddStrikeDialog } from './staff-report/AddStrikeDialog';
import { UserInspectionDialog } from './staff-report/UserInspectionDialog';
import { OverwatchNotesDialog } from './staff-report/OverwatchNotesDialog';
import { Permissions } from '@shiftly/shared/src/permissions';

export function StaffReportSpreadsheet() {
  const [isLoading, setIsLoading] = useState(true);
  const [staffReportData, setStaffReportData] = useState<Record<string, ShiftPatternData[]>>({});
  const [userNotesData, setUserNotesData] = useState<Map<string, StaffReportUserNotes>>(new Map());
  const [overwatchRequests, setOverwatchRequests] = useState<Map<string, OverwatchRequest>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('master');
  const [shiftCodeFilter, setShiftCodeFilter] = useState<string>('all');
  const [payClassFilter, setPayClassFilter] = useState<string>('all');
  const [date] = useState<DateTime>(() => {
    const now = DateTime.utc();
    const dayOfMonth = now.day;

    // If we're in the first 4 days of the month, use the previous month
    if (dayOfMonth <= 4) {
      return now.minus({ months: 1 }).startOf('month');
    }

    return now;
  });
  const { start: startDate, end: endDate } = useMemo(() => getMonthBoundary(date), [date]);
  
  // Calculate 30-day rolling window for shift averages using 6am boundaries
  const { rollingStart, rollingEnd } = useMemo(() => {
    const now = DateTime.utc();

    // Set end point to 6am on current day
    const endPoint = now.set({ hour: 6, minute: 0, second: 0, millisecond: 0 });

    // Set start point to 6am thirty days ago
    const startPoint = endPoint.minus({ days: 30 });

    return {
      rollingStart: startPoint,
      rollingEnd: endPoint
    };
  }, []);
  
  // Add Strike Dialog state
  const [addStrikeDialog, setAddStrikeDialog] = useState<{
    isOpen: boolean;
    user_id: string;
    userName: string;
    currentStrikeCount: number;
  }>({ isOpen: false, user_id: '', userName: '', currentStrikeCount: 0 });

  // User Inspection Dialog state
  const [userInspectionDialog, setUserInspectionDialog] = useState<{
    isOpen: boolean;
    user_id: string;
    userName: string;
    shiftPattern: string;
    model_id: string;
    modelName: string;
  }>({ isOpen: false, user_id: '', userName: '', shiftPattern: '', model_id: '', modelName: '' });

  // Overwatch Notes Dialog state
  const [overwatchNotesDialog, setOverwatchNotesDialog] = useState<{
    isOpen: boolean;
    user_id: string;
    userName: string;
    shiftPattern: string;
    model_id: string;
    currentNotes: string;
  }>({ isOpen: false, user_id: '', userName: '', shiftPattern: '', model_id: '', currentNotes: '' });

  // Refs for listener management
  const unsubscribeListenersRef = useRef<(() => void)[]>([]);
  const overwatchListenerRef = useRef<(() => void) | null>(null);
  const lastSaveTimeRef = useRef<DateTime>(DateTime.utc());

  // Get current user
  const { user } = useAuth();

  // Fetch all models to get model IDs
  const { data: models = [], isLoading: modelsLoading } = useModels();
  const modelIds = useMemo(() => models.map(model => model.id), [models]);

  // Fetch all users to get user names
  const { data: users = [], isLoading: usersLoading } = useUsers();
  
  // Fetch all roles to check permissions
  const { data: roles = [], isLoading: rolesLoading } = useRolesWithPermissions();
  
  // Create user options for the command popover - only users with STAFF_REPORT permission
  const userOptions = useMemo(() => {
    if (rolesLoading || usersLoading) return [];
    
    return users
      .filter(user => {
        // Check if any of the user's roles have the STAFF_REPORT permission
        const userRoles = user.roles || [];
        return userRoles.some(roleId => {
          const role = roles.find(r => r.id === roleId);
          return role?.permissions?.includes(Permissions.USERS_STAFF_REPORT);
        });
      })
      .map(user => ({
        value: user.id,
        label: user.full_name || user.email
      }));
  }, [users, roles, usersLoading, rolesLoading]);


  // Calculate the maximum span needed for a single optimized fetch
  const { optimizedStart, optimizedEnd } = useMemo(() => {
    const minStart = rollingStart < startDate ? rollingStart : startDate;
    const maxEnd = rollingEnd > endDate ? rollingEnd : endDate;

    return {
      optimizedStart: minStart,
      optimizedEnd: maxEnd
    };
  }, [rollingStart, rollingEnd, startDate, endDate]);

  // Single optimized fetch for all shifts needed
  const optimizedShiftsFilter = useMemo(() => [
    { field: 'start_date_time', operator: 'gte' as const, value: optimizedStart },
    { field: 'start_date_time', operator: 'lte' as const, value: optimizedEnd },
    { field: 'shift_code', operator: 'neq' as const, value: 'COVER'},
    { field: 'submitted', operator: 'eq' as const, value: true }
  ], [optimizedStart, optimizedEnd]);

  const { data: allShifts = [], isLoading: allShiftsLoading } = useShifts({
    filters: optimizedShiftsFilter
  });

  // Client-side filtering using React Query for better caching
  const { data: { rollingShifts, monthShifts } = { rollingShifts: [], monthShifts: [] } } = useQuery({
    queryKey: ['staff-report-filtered-shifts'],
    queryFn: () => {
      const rolling = allShifts.filter(shift =>
        shift.start_date_time >= rollingStart &&
        shift.start_date_time <= rollingEnd
      );

      const month = allShifts.filter(shift =>
        shift.start_date_time >= startDate &&
        shift.start_date_time <= endDate
      );

      return {
        rollingShifts: rolling,
        monthShifts: month
      };
    },
    enabled: allShifts.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  });

  // Fetch recurring shifts for all models
  const { data: recurringShifts = [], isLoading: recurringShiftsLoading } = useModelsFixedShifts(modelIds);

  // Calculate shift pattern data when dependencies change
  useEffect(() => {
    if (modelsLoading || allShiftsLoading || recurringShiftsLoading || usersLoading || rolesLoading) {
      setIsLoading(true);
      return;
    }

    if (rollingShifts.length === 0 || recurringShifts.length === 0) {
      setStaffReportData({});
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        // Use rolling shifts for calculating averages
        const calculatedData = calculateShiftPatternData(rollingShifts, recurringShifts);
        setStaffReportData(calculatedData);

        // Get all unique user IDs from the calculated data
        const alluser_ids = Array.from(new Set(
          Object.values(calculatedData).flat().map(data => data.user_id)
        ));

        // Load notes data for all users
        if (alluser_ids.length > 0) {
          try {
            const notesData = await StaffReportNotesService.getMultipleUsersNotesData(alluser_ids);
            setUserNotesData(notesData);
          } catch (error) {
            console.error('Error loading notes data:', error);
          }
        }

        // Load overwatch requests
        try {
          const overwatchData = await StaffReportNotesService.getAllOverwatchRequests();
          
          // Handle duplicates by keeping only the most recently updated request
          const deduplicatedRequests = new Map<string, OverwatchRequest>();
          const duplicateTracker = new Map<string, { requestId: string; request: OverwatchRequest }>();
          
          overwatchData.forEach((request, requestId) => {
            const key = `${request.user_id}-${request.shiftPattern}-${request.model_id}`;
            const existing = duplicateTracker.get(key);
            
            // If no existing request or the current request was updated more recently, use it
            if (!existing || (request.lastUpdated && (!existing.request.lastUpdated || request.lastUpdated > existing.request.lastUpdated))) {
              // Remove old duplicate if it exists
              if (existing) {
                deduplicatedRequests.delete(existing.requestId);
              }
              duplicateTracker.set(key, { requestId, request });
              deduplicatedRequests.set(requestId, request);
            }
          });
          
          setOverwatchRequests(deduplicatedRequests);
        } catch (error) {
          console.error('Error loading overwatch requests:', error);
        }


      } catch (error) {
        console.error('Error calculating shift pattern data:', error);
        setStaffReportData({});
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [rollingShifts, monthShifts, recurringShifts, modelsLoading, allShiftsLoading, recurringShiftsLoading, usersLoading, rolesLoading]);

  // Define tabs
  const tabs = useMemo(() => [
    { id: 'master', label: 'Master Sheet' },
    { id: 'wkd1', label: 'WKD 1' },
    { id: 'wkd2', label: 'WKD 2' },
    { id: 'wknd1', label: 'WKND 1' },
    { id: 'wknd2', label: 'WKND 2' },
    { id: 'wknd3', label: 'WKND 3' },
    { id: 'overwatch', label: 'Overwatch' },
    { id: 'completed-overwatch', label: 'Completed Overwatch' },
  ], []);

  const isWeekendPattern = useCallback((shiftPattern: string) => {
    return shiftPattern.includes('6am-6pm') || shiftPattern.includes('6pm-6am');
  }, []);

  // Filter function based on active tab, shift code filter, and pay class filter
  const filterDataByTab = useCallback((patternData: ShiftPatternData[], tabId: string, model_id: string) => {
    let filteredData = patternData;

    // Apply shift code filter first
    if (shiftCodeFilter !== 'all') {
      filteredData = patternData.filter(data => {
        const pattern = data.shiftPattern.toLowerCase();
        switch (shiftCodeFilter) {
          case '6am-2pm':
            return pattern.includes('6am-2pm');
          case '2pm-10pm':
            return pattern.includes('2pm-10pm');
          case '10pm-6am':
            return pattern.includes('10pm-6am');
          case '6am-6pm':
            return pattern.includes('6am-6pm');
          case '6pm-6am':
            return pattern.includes('6pm-6am');
          default:
            return true;
        }
      });
    }

    // Apply pay class filter
    if (payClassFilter !== 'all') {
      const model = models.find(m => m.id === model_id);
      if (!model || model.pay_class !== payClassFilter) {
        return [];
      }
    }

    if (tabId === 'master') return filteredData;
    if (tabId === 'overwatch') {
      // For overwatch tab, show only the specific shift patterns that have overwatch requests that are NOT completed
      return filteredData.filter(data => {
        // Find ALL matching requests for this user/pattern/model combination
        const matchingRequests = Array.from(overwatchRequests.values()).filter(request => 
          request.user_id === data.user_id && 
          request.shiftPattern === data.shiftPattern &&
          request.model_id === model_id
        );
        
        if (matchingRequests.length > 0) {
          const hasIncompleteRequest = matchingRequests.some(req => req.completed !== true);
          const hasCompletedRequest = matchingRequests.some(req => req.completed === true);
          
          return hasIncompleteRequest && !hasCompletedRequest;
        }
        
        return false;
      });
    }
    if (tabId === 'completed-overwatch') {
      // For completed overwatch tab, show only the specific shift patterns that have completed overwatch requests
      return filteredData.filter(data => {
        // Find ALL matching requests for this user/pattern/model combination
        const matchingRequests = Array.from(overwatchRequests.values()).filter(request => 
          request.user_id === data.user_id && 
          request.shiftPattern === data.shiftPattern &&
          request.model_id === model_id
        );
        
        if (matchingRequests.length > 0) {
          // Only show if there's at least one completed request AND no incomplete requests
          const hasIncompleteRequest = matchingRequests.some(req => req.completed !== true);
          const hasCompletedRequest = matchingRequests.some(req => req.completed === true);
          
          return hasCompletedRequest && !hasIncompleteRequest;
        }
        
        return false;
      });
    }

    const model = models.find(m => m.id === model_id);
    if (!model) return [];

    return filteredData.filter(data => {
      const isWeekend = isWeekendPattern(data.shiftPattern);

      switch (tabId) {
        case 'wkd1':
          return !isWeekend && model.pay_class === 'UK';
        case 'wkd2':
          return !isWeekend && model.pay_class === 'FP';
        case 'wknd1':
          return isWeekend && model.weekend_group === 1;
        case 'wknd2':
          return isWeekend && model.weekend_group === 2;
        case 'wknd3':
          return isWeekend && model.weekend_group === 3;
        default:
          return false;
      }
    });
  }, [models, isWeekendPattern, overwatchRequests, shiftCodeFilter, payClassFilter]);

  // Helper function to calculate average earnings for specific date ranges (used for completed overwatch calculations)
  const calculateEarningsForDateRange = useCallback((user_id: string, model_id: string, startDate: DateTime, endDate: DateTime) => {
    // Filter shifts for the specific user, model, and date range - use rollingShifts to get broader data range
    const userShifts = rollingShifts.filter(shift =>
      shift.user_id === user_id &&
      shift.model_id === model_id &&
      shift.submitted &&
      shift.start_date_time >= startDate &&
      shift.start_date_time <= endDate
    );

    if (userShifts.length === 0) {
      return { ppvPerShift: 0, noppvPerShift: 0, shiftCount: 0 };
    }

    const totalPPV = userShifts.reduce((sum, shift) => sum + (shift.ppv_earned || 0), 0);
    const totalNonPPV = userShifts.reduce((sum, shift) => sum + (shift.noppv_earned || 0), 0);

    return {
      ppvPerShift: totalPPV / userShifts.length,
      noppvPerShift: totalNonPPV / userShifts.length,
      shiftCount: userShifts.length
    };
  }, [rollingShifts]);

  // Create spreadsheet data
  const spreadsheetData = useMemo(() => {
    const rows: any[] = [];


    // Sort models alphabetically by name
    const sortedModelEntries = Object.entries(staffReportData).sort(([modelIdA], [modelIdB]) => {
      const modelA = models.find(m => m.id === modelIdA);
      const modelB = models.find(m => m.id === modelIdB);
      const nameA = modelA?.name || modelIdA;
      const nameB = modelB?.name || modelIdB;
      return nameA.localeCompare(nameB);
    });

    // Add data rows grouped by model
    sortedModelEntries.forEach(([model_id, patternData]) => {
      const model = models.find(m => m.id === model_id);
      const modelName = model?.name || model_id;

      // Filter data based on active tab
      const filteredPatternData = filterDataByTab(patternData, activeTab, model_id);

      // Skip this model if no data matches the current tab filter
      if (filteredPatternData.length === 0) return;

      // Add model name row
      const modelRow: any = {
        'Shift Pattern': modelName.toUpperCase(),
        'User Name': '',
        'Model Name': '',
        '$ppv/shift': '',
        '$noppv/shift': '',
        'Start Date': '',
        'Notes': '',
        isModelRow: true
      };

      if (activeTab === 'overwatch') {
        modelRow['Assigned Overwatcher'] = '';
        modelRow['Date Time'] = '';
        modelRow['Completed'] = '';
      } else if (activeTab === 'completed-overwatch') {
        modelRow['$ppv/shift (7 days)'] = '';
        modelRow['$noppv/shift (7 days)'] = '';
        modelRow['Assigned Overwatcher'] = '';
        modelRow['Completed At'] = '';
      } else {
        modelRow['User'] = '';
        modelRow['Strikes'] = '';
      }
      
      rows.push(modelRow);

      // Add shift pattern rows for this model
      filteredPatternData.forEach(data => {
        const user = users.find(u => u.id === data.user_id);
        const userName = user?.full_name || data.user_id;

        const userNotes = userNotesData.get(data.user_id);
        
        // Find overwatch request for this specific user and shift pattern
        const matchingRequests = Array.from(overwatchRequests.values())
          .filter(request => request.user_id === data.user_id && request.shiftPattern === data.shiftPattern && request.model_id === model_id);
        
        // For display purposes, prioritize incomplete requests for overwatch tab, completed for completed tab
        let overwatchRequest = null;
        if (activeTab === 'overwatch') {
          // Find the incomplete request if there are duplicates
          overwatchRequest = matchingRequests.find(req => req.completed !== true) || matchingRequests[0];
        } else if (activeTab === 'completed-overwatch') {
          // Find the completed request if there are duplicates
          overwatchRequest = matchingRequests.find(req => req.completed === true) || matchingRequests[0];
        } else {
          overwatchRequest = matchingRequests[0];
        }

        // Calculate earnings for different date ranges based on completedAt date
        let ppvBefore = '$0.00', noppvBefore = '$0.00', ppv7Days = 'N/A', noppv7Days = 'N/A';

        if (activeTab === 'completed-overwatch' && overwatchRequest?.completedAt) {
          const completedDate = overwatchRequest.completedAt;

          // Calculate average for shifts from 30 days before completion up to completedAt
          const thirtyDaysBeforeCompletion = completedDate.minus({ days: 30 });
          const dayBefore = completedDate.minus({ days: 1 });
          const beforeEarnings = calculateEarningsForDateRange(data.user_id, model_id, thirtyDaysBeforeCompletion, dayBefore);
          ppvBefore = `$${beforeEarnings.ppvPerShift.toFixed(2)}`;
          noppvBefore = `$${beforeEarnings.noppvPerShift.toFixed(2)}`;

          // Calculate average for 7 days after completedAt (new columns)
          const sevenDaysAfter = completedDate.plus({ days: 7 });
          const now = DateTime.utc();

          if (now >= sevenDaysAfter) {
            // 7 days have passed, calculate the average
            const after7DaysEarnings = calculateEarningsForDateRange(data.user_id, model_id, completedDate, sevenDaysAfter);
            ppv7Days = after7DaysEarnings.shiftCount > 0 ? `$${after7DaysEarnings.ppvPerShift.toFixed(2)}` : '$0.00';
            noppv7Days = after7DaysEarnings.shiftCount > 0 ? `$${after7DaysEarnings.noppvPerShift.toFixed(2)}` : '$0.00';
          } else {
            // 7 days haven't passed yet
            ppv7Days = `Available ${sevenDaysAfter.toFormat('dd/MM/yyyy')}`;
            noppv7Days = `Available ${sevenDaysAfter.toFormat('dd/MM/yyyy')}`;
          }
        }

        const userStartDate = user?.start_date || user?.created_at;

        const baseRow: any = {
          'Shift Pattern': data.shiftPattern,
          'User Name': userName,
          'Model Name': modelName,
          '$ppv/shift': activeTab === 'completed-overwatch' ? ppvBefore : `$${data.dollarPerShift.toFixed(2)}`,
          '$noppv/shift': activeTab === 'completed-overwatch' ? noppvBefore : `$${data.noppvPerShift.toFixed(2)}`,
          'Start Date': userStartDate && DateTime.isDateTime(userStartDate) ? userStartDate.toFormat('dd/MM/yyyy') : '',
          'Notes': (activeTab === 'overwatch' || activeTab === 'completed-overwatch') ? (overwatchRequest?.notes || '') : (userNotes?.notes || ''),
          isModelRow: false,
          user_id: data.user_id,
          model_id: model_id,
          modelName: modelName,
          // Add 7-day columns for completed overwatch tab
          ...(activeTab === 'completed-overwatch' && {
            '$ppv/shift (7 days)': ppv7Days,
            '$noppv/shift (7 days)': noppv7Days
          })
        };

        if (activeTab === 'overwatch' || activeTab === 'completed-overwatch') {
          // Add overwatch-specific columns
          // Get the overwatcher's full name
          const overwatcherUser = overwatchRequest?.assignedOverwatcher ? 
            users.find(u => u.id === overwatchRequest.assignedOverwatcher) : null;
          const overwatcherName = overwatcherUser?.full_name || overwatchRequest?.assignedOverwatcher || '';
          
          const overwatchRow: any = {
            ...baseRow,
            'Assigned Overwatcher': overwatcherName
          };
          
          if (activeTab === 'overwatch') {
            // Add Date Time and Completed only for overwatch tab
            // Pass timestamps directly from database
            overwatchRow['Date Time'] = overwatchRequest?.dateTime || null;
            overwatchRow['Completed'] = overwatchRequest ? overwatchRequest.completed : false;
          } else if (activeTab === 'completed-overwatch') {
            // Add Completed At only for completed overwatch tab
            // Pass timestamps directly from database
            overwatchRow['Completed At'] = overwatchRequest?.completedAt || null;
          }
          
          rows.push(overwatchRow);
        } else {
          // Calculate total strikes
          const strikeCount = [userNotes?.strike1, userNotes?.strike2, userNotes?.strike3]
            .filter(strike => strike && strike.trim() !== '').length;
          
          // Add regular columns for other tabs
          rows.push({
            ...baseRow,
            'User': userName, // Keep backward compatibility
            'Strikes': strikeCount,
            // Keep the individual strike data for internal tracking
            strike1: userNotes?.strike1 || '',
            strike2: userNotes?.strike2 || '',
            strike3: userNotes?.strike3 || ''
          });
        }
      });
    });

    return rows;
  }, [staffReportData, models, users, userNotesData, overwatchRequests, activeTab, filterDataByTab, calculateEarningsForDateRange]);

  // Set up real-time listeners for user notes
  useEffect(() => {
    if (!user?.id) return;

    // Get all unique user IDs from the calculated data
    const alluser_ids = Array.from(new Set(
      Object.values(staffReportData).flat().map(data => data.user_id)
    ));

    if (alluser_ids.length === 0) return;

    // Clear any existing listeners
    unsubscribeListenersRef.current.forEach(unsubscribe => unsubscribe());
    unsubscribeListenersRef.current = [];

    // Listen to all users' notes data with a single subscription
    const unsubscribe = StaffReportNotesService.listenToMultipleUsersNotes(
      alluser_ids,
      (user_idFromListener: string, userData: StaffReportUserNotes | null) => {
        // Always update the user notes data - don't skip our own updates
        // The UI should reflect the current state from RTDB
        setUserNotesData(prevData => {
          const newData = new Map(prevData);
          if (userData) {
            newData.set(user_idFromListener, userData);
          } else {
            newData.delete(user_idFromListener);
          }
          return newData;
        });
      }
    );

    unsubscribeListenersRef.current.push(unsubscribe);

    // Set up overwatch requests listener
    if (overwatchListenerRef.current) {
      overwatchListenerRef.current();
    }
    
    overwatchListenerRef.current = StaffReportNotesService.listenToOverwatchRequests(
      (requests: Map<string, OverwatchRequest>) => {
        console.log('Received overwatch requests update:', requests);
        // Handle duplicates by keeping only the most recently updated request
        const deduplicatedRequests = new Map<string, OverwatchRequest>();
        const duplicateTracker = new Map<string, { requestId: string; request: OverwatchRequest }>();

        requests.forEach((request, requestId) => {
          const key = `${request.user_id}-${request.shiftPattern}-${request.model_id}`;
          const existing = duplicateTracker.get(key);

          // If no existing request or the current request was updated more recently, use it
          if (!existing || (request.lastUpdated && (!existing.request.lastUpdated || request.lastUpdated > existing.request.lastUpdated))) {
            // Remove old duplicate if it exists
            if (existing) {
              deduplicatedRequests.delete(existing.requestId);
            }
            duplicateTracker.set(key, { requestId, request });
            deduplicatedRequests.set(requestId, request);
          }
        });

        setOverwatchRequests(deduplicatedRequests);
      }
    );


    // Cleanup function
    return () => {
      unsubscribeListenersRef.current.forEach(unsubscribe => unsubscribe());
      unsubscribeListenersRef.current = [];
      if (overwatchListenerRef.current) {
        overwatchListenerRef.current();
        overwatchListenerRef.current = null;
      }
    };
  }, [staffReportData, user?.id]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeListenersRef.current.forEach(unsubscribe => unsubscribe());
      if (overwatchListenerRef.current) {
        overwatchListenerRef.current();
      }
    };
  }, []);

  // Helper function to convert display name to user ID for assigned overwatcher
  const getOverwatcherIdFromName = useCallback((displayName: string): string => {
    if (!displayName) return '';
    const option = userOptions.find(opt => opt.label === displayName);
    return option?.value || displayName; // Fallback to original value if not found
  }, [userOptions]);

  // Handle data changes
  const handleDataChange = useCallback(async (newData: any[]) => {
    if (!user?.id || isSaving) {
      return;
    }

    if (activeTab === 'overwatch' || activeTab === 'completed-overwatch') {
      // Handle overwatch-specific updates
      const overwatchUpdates: Array<{
        user_id: string;
        notes?: string;
        assignedOverwatcher?: string;
        dateTime?: string;
        completed?: boolean;
      }> = [];

      newData.forEach((row, index) => {
        const originalRow = spreadsheetData[index];
        const user_id = row.user_id || originalRow?.user_id;

        if (!user_id || originalRow?.isModelRow) return;

        const newNotes = activeTab === 'overwatch' ? (row.G || '') : (row.I || '');
        const newAssignedOverwatcherName = activeTab === 'overwatch' ? (row.H || '') : (row.J || '');
        const newAssignedOverwatcher = getOverwatcherIdFromName(newAssignedOverwatcherName);
        // Preserve dateTime as number/null, handle 0 as valid timestamp
        const newDateTime = activeTab === 'overwatch' ? 
          (row.I !== undefined && row.I !== '' ? row.I : null) : 
          undefined; // undefined means don't update this field
        const newCompleted = activeTab === 'overwatch' 
          ? (row.J === true || row.J === 'true' || row.J === 'Yes' || row.J === 1)
          : activeTab === 'completed-overwatch' 
            ? true 
            : false;

        const originalNotes = originalRow['Notes'] || '';
        const originalAssignedOverwatcherName = originalRow['Assigned Overwatcher'] || '';
        const originalAssignedOverwatcher = getOverwatcherIdFromName(originalAssignedOverwatcherName);
        const originalDateTime = originalRow['Date Time'] ?? null;
        const originalCompleted = originalRow['Completed'] === true;

        // Only check dateTime if it's defined (overwatch tab)
        const dateTimeChanged = newDateTime !== undefined && newDateTime !== originalDateTime;

        if (newNotes !== originalNotes || newAssignedOverwatcher !== originalAssignedOverwatcher || dateTimeChanged || newCompleted !== originalCompleted) {
          overwatchUpdates.push({
            user_id,
            notes: newNotes,
            assignedOverwatcher: newAssignedOverwatcher,
            dateTime: newDateTime,
            completed: newCompleted
          });
        }
      });

      if (overwatchUpdates.length > 0) {
        try {
          setIsSaving(true);

          // Update overwatch requests
          for (const update of overwatchUpdates) {
            const shiftPattern = spreadsheetData.find(row => row.user_id === update.user_id && !row.isModelRow)?.['Shift Pattern'];
            const modelName = spreadsheetData.find(row => row.user_id === update.user_id && !row.isModelRow)?.['Model Name'];
            const model = models.find(m => m.name === modelName);
            const model_id = model?.id || modelName || '';
            
            // Find the specific request we're updating - prioritize incomplete ones for overwatch tab
            const matchingRequests = Array.from(overwatchRequests.entries())
              .filter(([_, request]) => request.user_id === update.user_id && request.shiftPattern === shiftPattern && request.model_id === model_id);
            
            let targetEntry = null;
            if (activeTab === 'overwatch') {
              // For overwatch tab, update the incomplete request
              targetEntry = matchingRequests.find(([_, req]) => req.completed !== true) || matchingRequests[0];
            } else {
              // For completed tab, update the completed request
              targetEntry = matchingRequests.find(([_, req]) => req.completed === true) || matchingRequests[0];
            }
            
            if (targetEntry) {
              const [requestId, overwatchRequest] = targetEntry;
              
              if (requestId) {
                const update_data: any = {
                  notes: update.notes,
                  assignedOverwatcher: update.assignedOverwatcher,
                  completed: update.completed
                };
                
                // If marking as completed, add completedAt timestamp
                if (update.completed && !overwatchRequest.completed) {
                  update_data.completedAt = DateTime.now();
                } else if (!update.completed && overwatchRequest.completed) {
                  // If uncompleting, remove completedAt
                  update_data.completedAt = null;
                }
                
                // Handle dateTime - only update if it's defined (not undefined)
                if (update.dateTime !== undefined) {
                  update_data.dateTime = update.dateTime;
                }
                
                await StaffReportNotesService.updateOverwatchRequest(
                  requestId,
                  update_data,
                  user.id
                );
              }
            }
          }
        } catch (error) {
          console.error('Error updating overwatch requests:', error);
        } finally {
          setIsSaving(false);
        }
      }
      return;
    }

    // Find what changed by comparing with current spreadsheet data
    const userUpdates: Array<{
      user_id: string;
      updates: {
        notes?: string;
        strike1?: string;
        strike2?: string;
        strike3?: string;
      };
    }> = [];

    newData.forEach((row, index) => {
      const originalRow = spreadsheetData[index];
      const user_id = row.user_id || originalRow?.user_id;

      if (!user_id || originalRow?.isModelRow) return;

      // Check if any editable fields have changed
      const newNotes = row.F || '';
      // Strikes are now read-only in the spreadsheet, so we don't update them from the grid

      const originalNotes = originalRow['Notes'] || '';

      if (newNotes !== originalNotes) {
        userUpdates.push({
          user_id,
          updates: {
            notes: newNotes
          }
        });
      }
    });

    if (userUpdates.length > 0) {
      try {
        setIsSaving(true);

        setUserNotesData(prevData => {
          const newData = new Map(prevData);
          userUpdates.forEach(({ user_id, updates }) => {
            const currentData = newData.get(user_id);
            const updatedData: StaffReportUserNotes = {
              user_id,
              notes: '',
              strike1: currentData?.strike1 || '',
              strike2: currentData?.strike2 || '',
              strike3: currentData?.strike3 || '',
              ...currentData,
              ...updates,
              lastUpdated: DateTime.now(),
              lastUpdatedBy: user.id
            };
            newData.set(user_id, updatedData);
          });
          return newData;
        });

        // Save to RTDB - listeners will sync any conflicts
        await StaffReportNotesService.updateMultipleUsersNotesData(
          userUpdates,
          user.id
        );

        lastSaveTimeRef.current = DateTime.now();
      } catch (error) {
        console.error('Error saving data changes:', error);
        // Revert optimistic updates on error by reloading from RTDB
        const alluser_ids = Array.from(new Set(
          Object.values(staffReportData).flat().map(data => data.user_id)
        ));
        if (alluser_ids.length > 0) {
          try {
            const notesData = await StaffReportNotesService.getMultipleUsersNotesData(alluser_ids);
            setUserNotesData(notesData);
          } catch (reloadError) {
            console.error('Error reloading notes data after save failure:', reloadError);
          }
        }
      } finally {
        setIsSaving(false);
      }
    }
  }, [user?.id, isSaving, spreadsheetData, staffReportData, activeTab, overwatchRequests, models, getOverwatcherIdFromName]);

  const getCellStyle = useCallback((_rowIdx: number, colKey: string, value: any, rowData?: any) => {
    if (rowData?.isModelRow) {
      return { backgroundColor: '#374151', color: '#ffffff', fontWeight: 700, fontSize: '14px' };
    }

    let textColor = '#111827';
    let fontWeight = 400;
    let backgroundColor = undefined;
    
    // Color code strikes column
    if (colKey === 'G' && activeTab !== 'overwatch' && activeTab !== 'completed-overwatch' && !rowData?.isModelRow) {
      fontWeight = 700;
      const strikeCount = value || 0;
      if (strikeCount === 2) {
        textColor = '#f59e0b';
      } else if (strikeCount >= 3) {
        textColor = '#ef4444';
      } else {
        textColor = '#10b981';
      }
    }

    // Helper function to parse currency values like $10,333.55
    const parseCurrency = (value: string) => {
      if (!value || typeof value !== 'string') return 0;
      // Remove $ and commas, then parse as float
      return parseFloat(value.replace(/[$,]/g, '')) || 0;
    };


    // Red highlighting for completed-overwatch sheet when after earnings < before earnings
    // Green highlighting for completed-overwatch sheet when after earnings > before earnings (improvement)
    if (activeTab === 'completed-overwatch' && !rowData?.isModelRow) {
      if ((colKey === 'F' || colKey === 'G') && rowData) { // $ppv/shift (7 days) or $noppv/shift (7 days) columns
        const beforePPV = parseCurrency(rowData['$ppv/shift']);
        const afterPPV = parseCurrency(rowData['$ppv/shift (7 days)']);
        const beforeNonPPV = parseCurrency(rowData['$noppv/shift']);
        const afterNonPPV = parseCurrency(rowData['$noppv/shift (7 days)']);
        
        // Check if the column we're styling has lower earnings than before (red highlighting)
        if (colKey === 'F' && afterPPV < beforePPV && afterPPV > 0) {
          backgroundColor = '#fecaca'; // Light red background
        } else if (colKey === 'G' && afterNonPPV < beforeNonPPV && afterNonPPV > 0) {
          backgroundColor = '#fecaca'; // Light red background
        }
        // Check if the column we're styling has higher earnings than before (green highlighting for improvement)
        else if (colKey === 'F' && afterPPV > beforePPV && afterPPV > 0) {
          backgroundColor = '#dcfce7'; // Light green background
        } else if (colKey === 'G' && afterNonPPV > beforeNonPPV && afterNonPPV > 0) {
          backgroundColor = '#dcfce7'; // Light green background
        }
      }
    }

    // Style weekend rows differently
    if (rowData && !rowData.isModelRow && rowData['Shift Pattern'] && isWeekendPattern(rowData['Shift Pattern'])) {
      return { backgroundColor: backgroundColor || '#e5e7eb', color: textColor, fontWeight: fontWeight };
    }

    // Style weekday rows
    if (rowData && !rowData.isModelRow && rowData['Shift Pattern'] && !isWeekendPattern(rowData['Shift Pattern'])) {
      return { backgroundColor: backgroundColor || '#f3f4f6', color: textColor, fontWeight: fontWeight };
    }

    return { backgroundColor, color: textColor, fontWeight };
  }, [isWeekendPattern, activeTab]);

  // Custom context menu items
  const customContextMenuItems: CustomContextMenuItem[] = useMemo(() => {
    const items: CustomContextMenuItem[] = [];

    // Add "Inspect User" option - available on all tabs
    items.push({
      id: 'inspect-user',
      label: 'Inspect User',
      icon: <Info size={16} />,
      onClick: async (rowData, _rowIndex) => {
        if (rowData && !rowData.isModelRow && rowData.user_id) {
          const user_id = rowData.user_id;
          const userName = rowData['B'] || rowData['User Name'] || user_id;
          const shiftPattern = rowData['A'] || rowData['Shift Pattern'];
          const model_id = rowData.model_id;
          const modelName = rowData.modelName || rowData['C'] || rowData['Model Name'] || '';
          
          setUserInspectionDialog({
            isOpen: true,
            user_id,
            userName,
            shiftPattern,
            model_id,
            modelName
          });
        }
      },
      disabled: false
    });

    // Add "Add Strike" option only on non-overwatch tabs
    if (activeTab !== 'overwatch' && activeTab !== 'completed-overwatch') {
      items.push({
        id: 'add-strike',
        label: 'Add Strike',
        icon: <AlertCircle size={16} />,
        onClick: async (rowData, _rowIndex) => {
          if (rowData && !rowData.isModelRow && rowData.user_id) {
            const user_id = rowData.user_id;
            const userName = rowData['B'] || rowData['User Name'] || user_id;
            const userNotes = userNotesData.get(user_id);
            const currentStrikeCount = [userNotes?.strike1, userNotes?.strike2, userNotes?.strike3]
              .filter(strike => strike && strike.trim() !== '').length;
            
            setAddStrikeDialog({
              isOpen: true,
              user_id,
              userName,
              currentStrikeCount
            });
          }
        },
        disabled: false
      });
    }

    // Add "Add to Overwatch" option only on non-overwatch tabs
    if (activeTab !== 'overwatch' && activeTab !== 'completed-overwatch') {
      items.push({
        id: 'add-overwatch-request',
        label: 'Add to Overwatch',
        icon: <Eye size={16} />,
        onClick: async (rowData, _rowIndex) => {
          if (rowData && !rowData.isModelRow && rowData.user_id && user?.id) {
            try {
              const user_id = rowData.user_id;
              const shiftPattern = rowData['A'] || rowData['Shift Pattern'];
              const model_id = rowData.model_id;
              const notes = '';
              await StaffReportNotesService.createOverwatchRequest(user_id, shiftPattern, model_id, notes, user.id);
            } catch (error) {
              console.error('Error creating Overwatch request:', error);
            }
          }
        },
        disabled: false
      });
    }

    // Add overwatch notes option when on the overwatch or completed overwatch tab
    if (activeTab === 'overwatch' || activeTab === 'completed-overwatch') {
      items.push({
        id: 'add-overwatch-notes',
        label: 'Add Overwatch Notes',
        icon: <Info size={16} />,
        onClick: async (rowData, _rowIndex) => {
          if (rowData && !rowData.isModelRow && rowData.user_id) {
            const user_id = rowData.user_id;
            const userName = rowData['B'] || rowData['User Name'] || user_id;
            const shiftPattern = rowData['A'] || rowData['Shift Pattern'];
            const model_id = rowData.model_id;
            const currentNotes = (activeTab === 'overwatch' ? rowData['G'] : rowData['I']) || rowData['Notes'] || '';
            
            setOverwatchNotesDialog({
              isOpen: true,
              user_id,
              userName,
              shiftPattern,
              model_id,
              currentNotes
            });
          }
        },
        disabled: false
      });
    }

    // Add remove overwatch request option when on the overwatch tab
    if (activeTab === 'overwatch' || activeTab === 'completed-overwatch') {
      items.push({
        id: 'remove-overwatch-request',
        label: 'Remove from Overwatch',
        icon: <Eye size={16} className="text-brand-red" />,
        onClick: async (rowData, _rowIndex) => {
          if (rowData && !rowData.isModelRow && rowData.user_id && user?.id) {
            try {
              // Find the overwatch request for this user
              const shiftPattern = rowData['A'] || rowData['Shift Pattern'];
              const model_id = rowData.model_id;
              const overwatchRequest = Array.from(overwatchRequests.entries())
                .find(([_, request]) => request.user_id === rowData.user_id && request.shiftPattern === shiftPattern && request.model_id === model_id);
              
              if (overwatchRequest) {
                const [requestId] = overwatchRequest;
                await StaffReportNotesService.deleteOverwatchRequest(requestId);
              }
            } catch (error) {
              console.error('Error removing Overwatch request:', error);
            }
          }
        },
        disabled: false
      });
    }

    return items;
  }, [user?.id, activeTab, overwatchRequests, models, userNotesData]);

  if (isLoading) {
    return <LoadingState />;
  }

  const handleAddStrike = async (reason: string) => {
    if (!user?.id || !addStrikeDialog.user_id) return;
    
    try {
      const user_id = addStrikeDialog.user_id;
      const userNotes = userNotesData.get(user_id);
      
      // Find the next available strike slot
      let strikeField: 'strike1' | 'strike2' | 'strike3' | null = null;
      if (!userNotes?.strike1 || userNotes.strike1.trim() === '') {
        strikeField = 'strike1';
      } else if (!userNotes?.strike2 || userNotes.strike2.trim() === '') {
        strikeField = 'strike2';
      } else if (!userNotes?.strike3 || userNotes.strike3.trim() === '') {
        strikeField = 'strike3';
      }
      
      if (strikeField) {
        const updates = {
          [strikeField]: reason
        };
        
        await StaffReportNotesService.updateMultipleUsersNotesData(
          [{ user_id, updates }],
          user.id
        );
      }
    } catch (error) {
      console.error('Error adding strike:', error);
    }
  };

  const handleOverwatchNotesUpdate = async (notes: string) => {
    if (!user?.id || !overwatchNotesDialog.user_id) return;
    
    try {
      const { user_id, shiftPattern, model_id } = overwatchNotesDialog;
      const overwatchRequest = Array.from(overwatchRequests.entries())
        .find(([_, request]) => request.user_id === user_id && request.shiftPattern === shiftPattern && request.model_id === model_id);
      
      if (overwatchRequest) {
        const [requestId] = overwatchRequest;
        await StaffReportNotesService.updateOverwatchRequest(
          requestId,
          { notes },
          user.id
        );
      }
    } catch (error) {
      console.error('Error updating overwatch notes:', error);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <Select value={shiftCodeFilter} onValueChange={setShiftCodeFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Shift Codes</SelectItem>
              <SelectItem value="6am-2pm">6am-2pm</SelectItem>
              <SelectItem value="2pm-10pm">2pm-10pm</SelectItem>
              <SelectItem value="10pm-6am">10pm-6am</SelectItem>
              <SelectItem value="6am-6pm">6am-6pm</SelectItem>
              <SelectItem value="6pm-6am">6pm-6am</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <Select value={payClassFilter} onValueChange={setPayClassFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Pay Classes</SelectItem>
              <SelectItem value="UK">UK</SelectItem>
              <SelectItem value="FP">FP</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <Card className="overflow-hidden">
        <CardContent className="p-0">
          <div className="flex flex-col">
            <Spreadsheet
              columns={activeTab === 'overwatch' ? 10 : (activeTab === 'completed-overwatch' ? 11 : 7)}
              rows={Math.max(spreadsheetData.length, 20)}
              data={spreadsheetData.map(row => {
                if (activeTab === 'overwatch') {
                  return {
                    A: row['Shift Pattern'],
                    B: row['User Name'],
                    C: row['Model Name'],
                    D: row['$ppv/shift'],
                    E: row['$noppv/shift'],
                    F: row['Start Date'],
                    G: row['Notes'],
                    H: row['Assigned Overwatcher'],
                    I: row['Date Time'],
                    J: row['Completed'],
                    isModelRow: row.isModelRow,
                    user_id: row.user_id,
                    model_id: row.model_id,
                    modelName: row.modelName
                  };
                } else if (activeTab === 'completed-overwatch') {
                  return {
                    A: row['Shift Pattern'],
                    B: row['User Name'],
                    C: row['Model Name'],
                    D: row['$ppv/shift'],
                    E: row['$noppv/shift'],
                    F: row['$ppv/shift (7 days)'],
                    G: row['$noppv/shift (7 days)'],
                    H: row['Start Date'],
                    I: row['Notes'],
                    J: row['Assigned Overwatcher'],
                    K: row['Completed At'] || '',
                    isModelRow: row.isModelRow,
                    user_id: row.user_id,
                    model_id: row.model_id,
                    modelName: row.modelName
                  };
                } else {
                  return {
                    A: row['Shift Pattern'],
                    B: row['User'] || row['User Name'],
                    C: row['$ppv/shift'],
                    D: row['$noppv/shift'],
                    E: row['Start Date'],
                    F: row['Notes'],
                    G: row['Strikes'] !== undefined ? String(row['Strikes']) : '0',
                    isModelRow: row.isModelRow,
                    user_id: row.user_id,
                    model_id: row.model_id,
                    modelName: row.modelName,
                    // Keep individual strike data for internal use
                    strike1: row.strike1,
                    strike2: row.strike2,
                    strike3: row.strike3
                  };
                }
              })}
              getCellStyle={(rowIdx, columnKey, value) => getCellStyle(rowIdx, columnKey, value, spreadsheetData[rowIdx])}
              isCellEditable={(rowIdx, columnKey) => {
                const rowData = spreadsheetData[rowIdx];
                if (rowData?.isModelRow) return false;
                if (activeTab === 'overwatch' || activeTab === 'completed-overwatch') {
                  // Check if this specific row has an overwatch request
                  const shiftPattern = rowData?.['Shift Pattern'];
                  const modelName = rowData?.['Model Name'];
                  const model = models.find(m => m.name === modelName);
                  const model_id = model?.id || modelName;
                  const hasOverwatchRequest = rowData?.user_id && Array.from(overwatchRequests.values())
                    .some(request => request.user_id === rowData.user_id && request.shiftPattern === shiftPattern && request.model_id === model_id);
                  if (!hasOverwatchRequest) return false;
                  // For overwatch tab, Notes, Assigned Overwatcher, Date Time, and Completed are editable
                  if (activeTab === 'overwatch') {
                    return ['G', 'H', 'I', 'J'].includes(columnKey);
                  }
                  // For completed overwatch tab, only Notes are editable
                  if (activeTab === 'completed-overwatch') {
                    return ['I'].includes(columnKey);
                  }
                  return false;
                } else {
                  // Only Notes column is editable for other tabs (strikes are managed via context menu)
                  return ['F'].includes(columnKey);
                }
              }}
              dropdownOptions={{}} // No dropdown options needed now
              textareaColumns={(activeTab === 'overwatch' || activeTab === 'completed-overwatch') ? (activeTab === 'overwatch' ? ['G'] : ['I']) : ['F']}
              checkboxColumns={activeTab === 'overwatch' ? ['J'] : []}
              datetimeColumns={activeTab === 'overwatch' ? ['I'] : (activeTab === 'completed-overwatch' ? ['K'] : [])}
              commandPopoverColumns={(activeTab === 'overwatch' || activeTab === 'completed-overwatch') ? (activeTab === 'overwatch' ? ['H'] : ['J']) : []}
              commandPopoverOptions={(activeTab === 'overwatch' || activeTab === 'completed-overwatch') ? (activeTab === 'overwatch' ? { H: userOptions } : { J: userOptions }) : {}}
              onDataChange={handleDataChange}
              editable={true}
              columnWidths={activeTab === 'overwatch' ?
                [150, 200, 150, 120, 120, 100, 175, 150, 170, 100] :
                (activeTab === 'completed-overwatch' ?
                  [150, 200, 150, 120, 120, 150, 150, 100, 175, 150, 170] :
                  [150, 200, 120, 120, 100, 175, 80])
              }
              rowHeight={35}
              showColumnHeaders={true}
              customColumnHeaders={activeTab === 'overwatch' ? [
                'Shift Pattern',
                'User Name',
                'Model Name',
                '$ppv/shift',
                '$noppv/shift',
                'Start Date',
                'Notes',
                'Assigned Overwatcher',
                'Date Time',
                'Completed'
              ] : (activeTab === 'completed-overwatch' ? [
                'Shift Pattern',
                'User Name',
                'Model Name',
                '$ppv/shift',
                '$noppv/shift',
                '$ppv/shift (after)',
                '$noppv/shift (after)',
                'Start Date',
                'Notes',
                'Assigned Overwatcher',
                'Completed At'
              ] : [
                'Shift Pattern',
                'User',
                '$ppv/shift',
                '$noppv/shift',
                'Start Date',
                'Notes',
                'Strikes'
              ])}
              fillHeight={true}
              buffers={{ fullscreen: 65, normal: 65 }}
              csvFilename={`shift-patterns-${activeTab}-${date.year}-${date.month.toString().padStart(2, '0')}.csv`}
              csvIncludeHeaders={true}
              className="rounded-b-none"
              customContextMenuItems={customContextMenuItems}
            />
            <SheetTabs
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
          </div>
        </CardContent>
      </Card>
      
      <AddStrikeDialog
        isOpen={addStrikeDialog.isOpen}
        onClose={() => setAddStrikeDialog({ isOpen: false, user_id: '', userName: '', currentStrikeCount: 0 })}
        onConfirm={handleAddStrike}
        userName={addStrikeDialog.userName}
        currentStrikeCount={addStrikeDialog.currentStrikeCount}
      />
      
      <UserInspectionDialog
        isOpen={userInspectionDialog.isOpen}
        onClose={() => setUserInspectionDialog({ isOpen: false, user_id: '', userName: '', shiftPattern: '', model_id: '', modelName: '' })}
        userName={userInspectionDialog.userName}
        user_id={userInspectionDialog.user_id}
        shiftPatternData={staffReportData[userInspectionDialog.model_id]?.find(data => data.user_id === userInspectionDialog.user_id && data.shiftPattern === userInspectionDialog.shiftPattern) || null}
        userNotes={userNotesData.get(userInspectionDialog.user_id) || null}
        modelName={userInspectionDialog.modelName}
        onAddStrike={async (user_id: string, reason: string) => {
          const userNotes = userNotesData.get(user_id);
          let strikeField: 'strike1' | 'strike2' | 'strike3' | null = null;
          if (!userNotes?.strike1 || userNotes.strike1.trim() === '') {
            strikeField = 'strike1';
          } else if (!userNotes?.strike2 || userNotes.strike2.trim() === '') {
            strikeField = 'strike2';
          } else if (!userNotes?.strike3 || userNotes.strike3.trim() === '') {
            strikeField = 'strike3';
          }
          
          if (strikeField && user?.id) {
            const updates = { [strikeField]: reason };
            await StaffReportNotesService.updateMultipleUsersNotesData(
              [{ user_id, updates }],
              user.id
            );
          }
        }}
        onRemoveStrike={async (user_id: string, strikeField: 'strike1' | 'strike2' | 'strike3') => {
          if (user?.id) {
            const updates = { [strikeField]: '' };
            await StaffReportNotesService.updateMultipleUsersNotesData(
              [{ user_id, updates }],
              user.id
            );
          }
        }}
        onUpdateNotes={async (user_id: string, notes: string) => {
          if (user?.id) {
            const updates = { notes };
            await StaffReportNotesService.updateMultipleUsersNotesData(
              [{ user_id, updates }],
              user.id
            );
          }
        }}
      />
      
      <OverwatchNotesDialog
        isOpen={overwatchNotesDialog.isOpen}
        onClose={() => setOverwatchNotesDialog({ isOpen: false, user_id: '', userName: '', shiftPattern: '', model_id: '', currentNotes: '' })}
        onConfirm={handleOverwatchNotesUpdate}
        userName={overwatchNotesDialog.userName}
        currentNotes={overwatchNotesDialog.currentNotes}
      />
    </div>
  );
}